import { trigger } from '@angular/animations';

import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { GalleryDto } from '@picflow/types';
import { Subscription } from 'rxjs';
import { CancelUploadDialogComponent } from '../../../account/dialogs/cancel-upload-dialog/cancel-upload-dialog.component';
import { FailedUploadsDialogComponent } from '../../../account/dialogs/failed-uploads-dialog/failed-uploads-dialog.component';
import { ShareGalleryDialogComponent } from '../../../account/dialogs/share-gallery-dialog/share-gallery-dialog.component';
import { ShareSubGalleryDialogComponent } from '../../../account/dialogs/share-sub-gallery-dialog/share-sub-gallery-dialog.component';
import { maxHeightIf } from '../../../animations/max-height.if.animation';
import { moveLeftIf } from '../../../animations/move-left.if.animation';
import { UploadObj } from '../../../classes/upload-obj.class';
import { GallerySettingsDialogComponent } from '../../../gallery/dialogs/gallery-settings-dialog/gallery-settings-dialog.component';
import { bigIntFraction } from '../../../lib/big-int-fraction';
import { debounce } from '../../../lib/debounce';
import { getAllRouteData } from '../../../lib/get-all-route-data';
import { removeIdPrefix } from '../../../lib/remove-id-prefix';
import { DialogService } from '../../../services/dialog.service';
import { GalleryService } from '../../../services/gallery.service';
import { ToastService } from '../../../services/toast.service';
import { UploadService } from '../../../services/upload.service';
import { RemoveIdPrefix } from '../../../shared-extra/pipes/remove-id-prefix.pipe';
import { ButtonMenuItemComponent } from '../../../shared/components/button-menu-item/button-menu-item.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { DataUnitsBigIntPipe } from '../../../shared/pipes/data-units-big-int.pipe';
import { UploadOrderPipe } from '../../pipes/upload-order.pipe';
import { UploadItemComponent } from '../upload-item/upload-item.component';

interface GalleryStats {
  status?: 'started' | 'error' | 'waiting' | 'done';
  totalUploadSize: bigint;
  uploadedSize: bigint;
  remainingSize: bigint;
}

@Component({
  selector: 'app-upload-info',
  templateUrl: './upload-info.component.html',
  styleUrls: ['./upload-info.component.scss'],
  animations: [
    trigger('uploadContent', maxHeightIf(220)),
    trigger('UploadInfo', moveLeftIf),
    trigger('uploadItem', maxHeightIf(45)),
  ],
  standalone: true,
  imports: [
    SvgComponent,
    ButtonDirective,
    ContextMenuComponent,
    ButtonMenuItemComponent,
    UploadItemComponent,
    RouterLink,
    RemoveIdPrefix,
    UploadOrderPipe,
    DataUnitsBigIntPipe,
  ],
})
export class UploadInfoComponent implements OnDestroy {
  minimized = false;
  baseTime: number;
  galleries: GalleryDto[] = [];
  galleryStats: Map<string, GalleryStats> = new Map();
  gallery: GalleryDto;
  onGalleryPage = false;
  showUploadInfo = false;
  showWarning = false;
  showMinimize = false;
  uploads: UploadObj[] = [];
  failedUploads: UploadObj[] = [];
  remainingUploadCount: number;
  showUploadDone = false;
  showUploadFailed = false;
  showMoreMenu = false;
  uploadedAssetCount: number;
  uploadedTotalSize: number;
  private routeWatcher: Subscription;
  private uploadWatcher: Subscription;

  constructor(
    private uploadService: UploadService,
    private router: Router,
    private galleryService: GalleryService,
    private dialog: DialogService,
    private route: ActivatedRoute,
    private toasts: ToastService
  ) {
    this.uploadWatcher = this.uploadService.onUploadEvent.subscribe(upload => {
      this.onUploadEvent();
      if (upload?.status === 'waiting' && upload.galleryId === this.routeGallery?.id) {
        // Switch to "gallery view" when an asset is uploaded and we are currently viewing the list of other galleries
        if (!this.gallery) this.onGallerychange();

        // When a new asset is uploaded, hide the done or failed dialog
        this.showUploadFailed = false;
        this.showUploadDone = false;
      }
    });

    this.routeWatcher = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        if (this.showUploadDone) this.close();
        this.showUploadDone = false;
        this.onGallerychange();
      }
    });
  }

  get uploadProgressPercent() {
    const stats = this.stats();
    if (!stats.uploadedSize || !stats.totalUploadSize) return 0;
    return bigIntFraction(stats.uploadedSize, stats.totalUploadSize) * 100;
  }

  private onGallerychange() {
    this.calcUploads('gallery-change');
  }

  // Debounced so that 500 assets uploaded won't trigger this method 500 times (instead only once)
  onUploadEvent = debounce(() => {
    this.calcUploads();
  });

  async calcUploads(reason?: string) {
    this.gallery = this.routeGallery;
    this.onGalleryPage = !!this.gallery;
    await this.getGalleries();
    if (!this.onGalleryPage && this.galleries.length === 1) this.gallery = this.galleries[0];

    if (this.gallery) this.uploads = this.getGalleryUploads(this.gallery);
    else this.uploads = this.uploadService.uploads;
    if (!this.uploads.length) this.gallery = undefined;

    this.galleries.forEach(gallery => {
      const uploads = this.getGalleryUploads(gallery);
      this.galleryStats.set(gallery.id, {
        status: this.getGalleryStatus(gallery),
        totalUploadSize: this.getTotalSize(uploads),
        uploadedSize: this.getUploadedSize(uploads),
        remainingSize: this.getRemainingSize(uploads),
      });
    });

    this.galleryStats.set('all', {
      totalUploadSize: this.getTotalSize(this.uploadService.uploads),
      uploadedSize: this.getUploadedSize(this.uploadService.uploads),
      remainingSize: this.getRemainingSize(this.uploadService.uploads),
    });

    const completedGalleries = this.galleries.filter(gallery => {
      return !this.uploadService.uploads.find(
        upload => ['started', 'waiting', 'processing'].includes(upload.status) && upload.galleryId === gallery.id
      );
    });

    const failedUploads = this.uploads.filter(u => u.status === 'error');
    if (failedUploads.length) this.failedUploads = failedUploads;
    this.remainingUploadCount = this.uploads.filter(u => !['done', 'processing'].includes(u.status)).length;
    if (this.gallery && completedGalleries.find(g => g.id === this.gallery.id)) {
      const successfulUploads = this.uploads.filter(u => u.status === 'done');
      this.uploadedAssetCount = successfulUploads.length;
      this.uploadedTotalSize = successfulUploads.reduce((t, v) => t + v.file.size, 0);

      if (this.uploads.find(u => !u.cancelled)) {
        if (successfulUploads.length) this.showUploadDone = true;
        else this.showUploadFailed = true;
      }
    }

    completedGalleries.forEach(gallery => {
      this.uploadService.removeUploads(gallery.id);
      if (!this.gallery || gallery.id !== this.gallery.id) {
        this.toasts.add('Gallery upload completed', 'success');
      }
      const index = this.galleries.findIndex(g => g === gallery);
      this.galleries.splice(index, 1);
    });

    this.showWarning =
      !!this.uploads.find(upload => upload.status === 'error') &&
      !this.uploads.find(upload => ['started', 'waiting'].includes(upload.status));
    if (!this.gallery && this.galleryStats.get('all').remainingSize) this.showWarning = false;

    this.showMinimize = !this.showWarning && this.onGalleryPage;

    // Unminimize when switching to the gallery that is uploading assets
    if (this.showMinimize && reason === 'gallery-change') this.minimized = false;

    this.showUploadInfo = !!this.uploadService.uploads.length;
    this.showMoreMenu = this.gallery && this.onGalleryPage;
  }

  close() {
    this.failedUploads = [];
    if (this.gallery) this.uploadService.removeUploads(this.gallery.id);
    this.gallery = undefined;
    this.showUploadDone = false;
    this.showUploadFailed = false;
    this.calcUploads();
  }

  toggleMinimized() {
    this.minimized = !this.minimized;
  }

  switchToGalleries() {
    this.gallery = undefined;
  }

  openGallerySettingsDialog() {
    this.dialog.open(GallerySettingsDialogComponent, { gallery: this.gallery });
    this.showUploadDone = false;
  }

  private async getGalleries() {
    const galleryIds = [];
    this.uploadService.uploads.forEach(
      upload => galleryIds.includes(upload.galleryId) || galleryIds.push(upload.galleryId)
    );

    // Filter out galleries that are no longer needed
    this.galleries.slice().forEach(gallery => {
      if (galleryIds.includes(gallery.id)) return;
      const index = this.galleries.findIndex(g => g === gallery);
      this.galleries.splice(index, 1);
    });

    const galleryIdsToFetch = galleryIds.filter(id => !this.galleries.find(g => g.id === id));

    const newGalleries = await Promise.all(galleryIdsToFetch.map(id => this.galleryService.getGallery(id)));
    newGalleries.forEach(g => {
      if (this.galleries.find(gallery => gallery.id === g.id)) return;
      this.galleries.push(g);
    });
  }

  private getTotalSize(uploads: UploadObj[]) {
    return uploads.filter(u => u.status !== 'error').reduce((t, v) => t + BigInt(v.file.size), 0n);
  }

  private getUploadedSize(uploads: UploadObj[]) {
    return uploads
      .filter(u => ['done', 'processing', 'started'].includes(u.status))
      .reduce((t, v) => t + BigInt(Math.round(v.file.size * (v.progress / 100))), 0n);
  }

  private getRemainingSize(uploads: UploadObj[]) {
    return uploads
      .filter(u => !['done', 'error', 'processing'].includes(u.status))
      .reduce((t, v) => t + BigInt(v.file.size), 0n);
  }

  private getGalleryUploads(gallery: GalleryDto) {
    return this.uploadService.uploads.filter(u => u.galleryId === gallery.id);
  }

  public stats(gallery = this.gallery): GalleryStats {
    if (!gallery) return this.galleryStats.get('all');
    return this.galleryStats.get(gallery.id) || <GalleryStats>{};
  }

  public getGalleryStatus(gallery: GalleryDto) {
    const uploads = this.getGalleryUploads(gallery);
    if (uploads.find(upload => upload.status === 'started')) return 'started';
    if (uploads.find(upload => upload.status === 'error')) return 'error';
    if (uploads.find(upload => upload.status === 'waiting')) return 'waiting';
    return 'done';
  }

  public dismissError(upload: UploadObj) {
    const index = this.uploadService.uploads.indexOf(upload);
    this.uploadService.uploads.splice(index, 1);
    this.calcUploads();
  }

  public async showFailedUploads() {
    const resp = await this.dialog.open(FailedUploadsDialogComponent, {
      failedUploads: this.failedUploads,
      gallery: this.gallery,
    });
    if (resp) {
      this.showUploadFailed = false;
      this.showUploadDone = false;
    }
  }

  public galleryAsUpload(gallery: GalleryDto): UploadObj {
    const stats = this.stats(gallery);
    const progress = bigIntFraction(stats.uploadedSize, stats.totalUploadSize) * 100;
    return <UploadObj>{
      status: stats.status,
      progress,
      file: {
        size: Number(stats.totalUploadSize),
        name: gallery.name || gallery.title || 'Untitled',
        type: 'fake/gallery',
      },
    };
  }

  public headerClick() {
    if (this.onGalleryPage) return;
    if (!this.gallery) return;
    this.router.navigate(['a', 'gallery', removeIdPrefix(this.gallery.id)]);
  }

  public async showCancelUploadDialog() {
    const gallery = this.gallery;
    const resp = await this.dialog.open(CancelUploadDialogComponent, {
      stats: { ...this.stats(), pending: this.uploads.filter(u => u.status === 'waiting').length },
    });
    if (resp) {
      this.uploadService.cancelUploads(gallery.id);
    }
  }

  private get routeGallery(): GalleryDto {
    const data = getAllRouteData(this.route.snapshot);
    if (data.gallery && data.sections) return data.gallery;
  }

  openShareDialog() {
    if (this.gallery.section) {
      this.dialog.open(ShareSubGalleryDialogComponent, { gallery: this.gallery });
    } else {
      this.dialog.open(ShareGalleryDialogComponent, { gallery: this.gallery });
    }
  }

  ngOnDestroy() {
    this.uploadWatcher.unsubscribe();
    this.routeWatcher.unsubscribe();
  }
}
